import { inherits as _inherits, createSuper as _createSuper, classCallCheck as _classCallCheck, defineProperty as _defineProperty, createClass as _createClass } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { createRef } from 'react';
import { lsgPre } from '../../config/prefix.js';
import { uniqueId } from '../../utils/DomUtils.js';
import { Key } from '../../utils/Keys.js';
import { CurrentSlider, defaultProps, SliderPresentation } from './SliderPresentation.js';

var SliderWrapper = /*#__PURE__*/function (_React$Component) {
  _inherits(SliderWrapper, _React$Component);

  var _super = _createSuper(SliderWrapper);

  function SliderWrapper(props) {
    var _this;

    _classCallCheck(this, SliderWrapper);

    _this = _super.call(this, props);
    _this.inputId = uniqueId("".concat(lsgPre, "-slider"));
    _this.mousedownOffsetX = 0; // TODO to simplify the logic, we might consider removing this feature

    _this.isMouseDrag = false;
    _this.afterMouseDrag = false;
    _this.hostRef = /*#__PURE__*/createRef();
    _this.lowerSliderThumbRef = /*#__PURE__*/createRef();
    _this.upperSliderThumbRef = /*#__PURE__*/createRef();
    _this.sliderTrackContainerRef = /*#__PURE__*/createRef();
    _this.sliderMarkerTrackColorDummyRef = /*#__PURE__*/createRef(); // we have to bind the listeners to the context, else the variables will be unknown in the eventlistener
    // so we wrap them in an arrow function.

    _this.eventListeners = {
      mousemove: function mousemove(event) {
        return _this.onMouseMove(event);
      },
      mouseup: function mouseup(event) {
        return _this.onDocumentMouseUp(event);
      },
      mouseleave: function mouseleave(event) {
        return _this.onDocumentMouseLeave(event);
      },
      touchmove: function touchmove(event) {
        return _this.onMouseMove(event);
      },
      touchend: function touchend(event) {
        return _this.onDocumentMouseUp(event);
      },
      touchcancel: function touchcancel(event) {
        return _this.onDocumentMouseUp(event);
      }
    };

    _this.registerMouseDownEventListeners = function () {
      Object.keys(_this.eventListeners).forEach(function (key) {
        window.addEventListener(key, _this.eventListeners[key], key === "touchmove" || key === "touchend" || key === "touchcancel" ? {
          passive: true
        } : undefined);
      });
    };

    _this.deregisterMouseDownEventListeners = function () {
      Object.keys(_this.eventListeners).forEach(function (key) {
        window.removeEventListener(key, _this.eventListeners[key]);
      });
    };

    _this.calculateValueToPercent = function (value, minValue, maxValue) {
      return (value - minValue) / (maxValue - minValue) * 100;
    };

    _this.calculatePercentToValue = function (percentage, minValue, maxValue) {
      return _this.tidyDecimals(minValue + (maxValue - minValue) / 100 * percentage);
    };

    _this.tidyDecimals = function (value) {
      return Math.round(value * 100000) / 100000;
    };

    _this.convertPercentageToValue = function (percentage) {
      return _this.tidyDecimals(_this.props.minValue + (_this.props.maxValue - _this.props.minValue) / 100 * percentage);
    };

    _this.getNearestStep = function (percentage) {
      // Input: Percentage (value / (maxValue-minValue) * 100)
      // Output: stepped Percentage
      var stepOnSliderInPercent = (_this.props.step || _this.props.precision) / (_this.props.maxValue - _this.props.minValue) * 100;
      var steppedValueInPercent = Math.round(percentage / stepOnSliderInPercent) * stepOnSliderInPercent;
      var lastSteppedValueInPercent = Math.floor(100 / stepOnSliderInPercent) * stepOnSliderInPercent;

      if (percentage > (100 + lastSteppedValueInPercent) / 2) {
        steppedValueInPercent = 100;
      }

      steppedValueInPercent = Math.min(steppedValueInPercent, 100);
      return steppedValueInPercent;
    };

    _this.getPercentageByMousePosition = function (e, slider) {
      var mouseX = e.clientX || e.changedTouches[0].clientX;

      var sliderLeft = _this.sliderTrackContainerRef.current.getBoundingClientRect().left;

      var sliderWidth = _this.sliderTrackContainerRef.current.getBoundingClientRect().width;

      var thumbWidth = _this.getThumbSliderWidth(slider);

      var percentage = (mouseX + thumbWidth / 2 - _this.mousedownOffsetX - sliderLeft) / sliderWidth * 100;
      percentage = Math.min(Math.max(percentage, 0), 100);

      if (_this.props.step || _this.props.precision) {
        return _this.getNearestStep(percentage);
      }

      return percentage;
    };

    _this.getClosestSlider = function (currentPercentMousePosition) {
      if (currentPercentMousePosition <= _this.state.lowerSliderThumbPercentage) {
        return CurrentSlider.Lower;
      }

      if (currentPercentMousePosition >= _this.state.upperSliderThumbPercentage) {
        return CurrentSlider.Upper;
      }

      var distanceLowerToPosition = currentPercentMousePosition - _this.state.lowerSliderThumbPercentage;
      var distanceLowerInPercent = distanceLowerToPosition * 100 / (_this.state.upperSliderThumbPercentage - _this.state.lowerSliderThumbPercentage);

      if (distanceLowerInPercent < 50) {
        return CurrentSlider.Lower;
      }

      return CurrentSlider.Upper;
    };

    _this.getThumbSliderWidth = function (slider, asPercent) {
      var sliderWidth = slider === CurrentSlider.Lower ? _this.lowerSliderThumbRef.current.getBoundingClientRect().width : _this.upperSliderThumbRef.current.getBoundingClientRect().width;
      return asPercent ? sliderWidth * 100 / _this.sliderTrackContainerRef.current.getBoundingClientRect().width : sliderWidth;
    };

    _this.resetValueByMissingOnChange = function () {
      _this.setState({
        lowerSliderThumbPercentage: _this.state.initialLowerPercentage
      });

      _this.setState({
        upperSliderThumbPercentage: _this.state.initialUpperPercentage
      });
    }; // Write values in state and public event functions


    _this.setValue = function (percentage, slider) {
      var value = _this.calculatePercentToValue(percentage, _this.props.minValue, _this.props.maxValue);

      if (_this.props.look === "singleSlider") {
        _this.setState({
          lowerSliderThumbPercentage: percentage
        });

        if (_this.props.onInput) {
          _this.props.onInput(value, _this.props.name);
        }

        if (_this.props.onChange) {
          _this.props.onChange(value, _this.props.name);
        }

        return; // rangeSlider-mode treatment below
      }

      var lowerValue = 0;
      var upperValue = 0;

      if (slider === CurrentSlider.Lower) {
        _this.setState({
          lowerSliderThumbPercentage: percentage
        });

        lowerValue = value;
        upperValue = _this.calculatePercentToValue(_this.state.upperSliderThumbPercentage, _this.props.minValue, _this.props.maxValue);
      } else {
        _this.setState({
          upperSliderThumbPercentage: percentage
        });

        lowerValue = _this.calculatePercentToValue(_this.state.lowerSliderThumbPercentage, _this.props.minValue, _this.props.maxValue);
        upperValue = value;
      }

      if (_this.props.onInput) {
        _this.props.onInput([lowerValue, upperValue], _this.props.name);
      }

      if (_this.props.onChange) {
        _this.props.onChange([lowerValue, upperValue], _this.props.name);
      }
    }; // Write values in state and public event function during tracking action


    _this.setTemporaryValue = function (percentage, slider) {
      var value = _this.calculatePercentToValue(percentage, _this.props.minValue, _this.props.maxValue);

      if (_this.props.look === "singleSlider") {
        _this.setState({
          lowerSliderThumbPercentage: percentage
        });

        if (_this.props.onInput) {
          _this.props.onInput(value, _this.props.name);
        }

        return; // rangeSlider-mode treatment below
      }

      var lowerValue = 0;
      var upperValue = 0;

      if (slider === CurrentSlider.Lower) {
        _this.setState({
          lowerSliderThumbPercentage: percentage
        });

        lowerValue = value;
        upperValue = _this.calculatePercentToValue(_this.state.upperSliderThumbPercentage, _this.props.minValue, _this.props.maxValue);
      } else {
        _this.setState({
          upperSliderThumbPercentage: percentage
        });

        lowerValue = _this.calculatePercentToValue(_this.state.lowerSliderThumbPercentage, _this.props.minValue, _this.props.maxValue);
        upperValue = value;
      }

      if (_this.props.onInput) {
        _this.props.onInput([lowerValue, upperValue], _this.props.name);
      }
    };

    _this.onMouseDown = function (event) {
      // Mousedown on slider thumb
      // do not call mouseup twice (touchend + mouseup)
      event.preventDefault(); // Disabled then exit

      if (_this.props.disabled) return;
      var focusOnSlider = CurrentSlider.None;

      if (event.currentTarget.id) {
        var postfix = event.currentTarget.id.substr(event.currentTarget.id.lastIndexOf("_") + 1);
        focusOnSlider = postfix;
      } // we need to tell the difference between a short mouseclick or a long mousedown (which is probably a drag).
      // a short mouse click moves the center of the thumb in the mouse click point,
      // even when clicked on the thumb itself
      // a long click on the thumb does not move the thumb unless it is dragged
      // this.sliderThumbMouseClickStartInMilliseconds = new Date().getTime();


      var thumbWidth = _this.getThumbSliderWidth(focusOnSlider);

      if (focusOnSlider === CurrentSlider.Upper) {
        _this.upperSliderThumbRef.current.focus();
      } else {
        _this.lowerSliderThumbRef.current.focus();
      } // TODO does not seem to work on click devices either.
      // if we have a touch device (no offsetX supported, let's take 0)


      _this.mousedownOffsetX = event.offSetX || thumbWidth / 2;

      _this.onDragStart();

      _this.setState({
        hasFocusOnSlider: focusOnSlider
      });
    };

    _this.onDragStart = function () {
      _this.isMouseDrag = true;
    };

    _this.onDocumentMouseUp = function (event) {
      // do not call mouseup twice (touchend + mouseup)
      event.preventDefault(); // Disabled then exit

      if (_this.props.disabled) return; // was it a mouseClick on the slider or a drag of the slider?
      // If it was a mouseclick, the slider should move to the click position, else to the dragged position.

      if (!_this.isMouseDrag) return;

      var percentage = _this.getPercentageByMousePosition(event, _this.state.hasFocusOnSlider);

      if (!_this.props.onChange) {
        window.requestAnimationFrame(_this.resetValueByMissingOnChange);
      }

      _this.afterMouseDrag = true;

      if (_this.props.look === "singleSlider") {
        _this.setValue(percentage, _this.state.hasFocusOnSlider);

        _this.isMouseDrag = false;
        return;
      } // Range slider treatment


      var definedStep = _this.props.step || _this.props.precision;
      var stepSliderInPercent = definedStep ? definedStep / (_this.props.maxValue - _this.props.minValue) * 100 : 1;
      percentage = _this.getCalculatedRangeSliderPosition(percentage, _this.state.hasFocusOnSlider, stepSliderInPercent);

      _this.setValue(percentage, _this.state.hasFocusOnSlider);

      _this.isMouseDrag = false;
    };

    _this.onDocumentMouseLeave = function (event) {
      if (_this.isMouseDrag) {
        _this.isMouseDrag = false;
      }

      _this.setValue(_this.getPercentageByMousePosition(event, _this.state.hasFocusOnSlider));
    };

    _this.onMouseMove = function (event) {
      // do not call mousemove twice (touchmove + mousemove)
      // event.preventDefault();
      if (!_this.isMouseDrag) return;

      var percentage = _this.getPercentageByMousePosition(event, _this.state.hasFocusOnSlider);

      if (_this.props.look === "singleSlider") {
        _this.setTemporaryValue(percentage, _this.state.hasFocusOnSlider);
      } // Range slider treatment


      var definedStep = _this.props.step || _this.props.precision;
      var stepSliderInPercent = definedStep ? definedStep / (_this.props.maxValue - _this.props.minValue) * 100 : 1;
      percentage = _this.getCalculatedRangeSliderPosition(percentage, _this.state.hasFocusOnSlider, stepSliderInPercent);

      _this.setTemporaryValue(percentage, _this.state.hasFocusOnSlider);
    };

    _this.onTrackClick = function (event) {
      // Disabled then exit
      if (_this.props.disabled) return;

      if (_this.afterMouseDrag) {
        _this.afterMouseDrag = false;
        return;
      }

      var currentPercentagePosition = _this.getPercentageByMousePosition(event, _this.state.hasFocusOnSlider);

      var chosenSlider = _this.state.hasFocusOnSlider;

      if (_this.props.look === "rangeSlider") {
        chosenSlider = _this.getClosestSlider(currentPercentagePosition);
      } else {
        chosenSlider = CurrentSlider.Lower;
      }

      if (!_this.isMouseDrag) {
        _this.setValue(currentPercentagePosition, chosenSlider);
      } else {
        _this.isMouseDrag = false;
      }

      if (chosenSlider === CurrentSlider.Lower) {
        _this.lowerSliderThumbRef.current.focus();
      } else {
        _this.upperSliderThumbRef.current.focus();
      }

      _this.setState({
        hasFocusOnSlider: chosenSlider
      });

      if (!_this.props.onChange) {
        window.requestAnimationFrame(_this.resetValueByMissingOnChange);
      }
    };

    _this.onFocus = function (event, name) {
      // Disabled then exit
      if (_this.props.disabled) return; // if (document.documentElement.getAttribute("data-whatinput") !== "keyboard" ) return;

      var postfix = event.target.id.substr(event.target.id.lastIndexOf("_") + 1); // code row below checks, whether postfix contains any hint of CurrentSlider-Enum

      if (postfix && !Object.values(CurrentSlider).includes(postfix)) return;
      var foccused = postfix;

      if (foccused === CurrentSlider.Lower) {
        _this.lowerSliderThumbRef.current.focus();
      }

      if (foccused === CurrentSlider.Upper) {
        _this.upperSliderThumbRef.current.focus();
      }

      _this.setState({
        hasFocusOnSlider: foccused
      });

      if (_this.props.onFocus) {
        _this.props.onFocus(event, name);
      }
    };

    _this.handleOnSliderBlur = function (event) {
      if (_this.props.disabled) return;
      if (_this.hostRef.current.contains(event.relatedTarget)) return;

      if (_this.props.onBlur) {
        _this.props.onBlur(event, _this.props.name);
      }

      _this.setState({
        hasFocusOnSlider: CurrentSlider.None
      });
    };

    _this.onHelperMinClick = function () {
      // Disabled then exit
      if (_this.props.disabled) return;

      _this.lowerSliderThumbRef.current.focus();

      _this.setValue(0, CurrentSlider.Lower); // lower presents always the smallest value

    };

    _this.onHelperMaxClick = function () {
      // Disabled then exit
      if (_this.props.disabled) return;
      var slider = CurrentSlider.None;

      if (_this.props.look === "rangeSlider") {
        _this.upperSliderThumbRef.current.focus();

        slider = CurrentSlider.Upper;
      } else {
        _this.lowerSliderThumbRef.current.focus();

        slider = CurrentSlider.Lower;
      }

      _this.setValue(100, slider);
    };

    _this.handleNavKeyPressed = function (newPercentage, oldPercentage, stepOnSliderInPercent, slider) {
      if (_this.tidyDecimals(newPercentage) === _this.tidyDecimals(oldPercentage)) return;
      var percentage = newPercentage;

      if (!_this.props.onChange) {
        window.requestAnimationFrame(_this.resetValueByMissingOnChange);
      }

      if (_this.props.look === "singleSlider") {
        percentage = Math.min(Math.max(percentage, 0), 100);

        _this.setValue(percentage, slider);

        return;
      } // Range slider treatment


      percentage = _this.getCalculatedRangeSliderPosition(percentage, slider, stepOnSliderInPercent);
      percentage = Math.min(Math.max(percentage, 0), 100);

      _this.setValue(percentage, slider);
    };

    _this.onKeyDown = function (ev) {
      var _keyFunctionCollectio;

      // listen to all keys except Tab
      if (_this.props.disabled || ev.key === Key.Tab) return;
      ev.preventDefault();
      if (_this.state.hasFocusOnSlider === CurrentSlider.None) return;
      var percentage;
      var currentSliderValue = _this.state.hasFocusOnSlider === CurrentSlider.Lower ? _this.props.look === "singleSlider" ? _this.props.value : _this.props.value[0] : _this.props.value[1];

      var currentSliderPercentage = _this.tidyDecimals(_this.calculateValueToPercent(currentSliderValue, _this.props.minValue, _this.props.maxValue));

      var definedStep = _this.props.step || _this.props.precision;
      var stepOnSliderInPercent = definedStep ? definedStep / (_this.props.maxValue - _this.props.minValue) * 100 : 1;
      var keyFunctionCollection = (_keyFunctionCollectio = {}, _defineProperty(_keyFunctionCollectio, Key.Home, function () {
        percentage = 0;

        _this.handleNavKeyPressed(percentage, currentSliderPercentage, stepOnSliderInPercent, _this.state.hasFocusOnSlider);
      }), _defineProperty(_keyFunctionCollectio, Key.End, function () {
        percentage = 100;

        if (_this.props.look === "singleSlider") {
          _this.handleNavKeyPressed(percentage, currentSliderPercentage, stepOnSliderInPercent, _this.state.hasFocusOnSlider);
        }

        if (_this.props.look === "rangeSlider" && _this.state.hasFocusOnSlider === CurrentSlider.Upper) {
          _this.handleNavKeyPressed(percentage, currentSliderPercentage, stepOnSliderInPercent, _this.state.hasFocusOnSlider);
        }
      }), _defineProperty(_keyFunctionCollectio, Key.ArrowLeft, function () {
        if (!definedStep) {
          percentage = currentSliderPercentage - 1;
        } else {
          percentage = _this.getNearestStep(currentSliderPercentage);

          if (_this.tidyDecimals(currentSliderPercentage) === 100) {
            // edge case: It's possible that the last percentage before 100 !== percentage - stepOnSliderInPercent
            // so take care of that.
            percentage = Math.floor(100 / _this.tidyDecimals(stepOnSliderInPercent)) * _this.tidyDecimals(stepOnSliderInPercent);

            if (percentage === 100) {
              percentage -= stepOnSliderInPercent;
            }
          } else if (_this.tidyDecimals(percentage) >= _this.tidyDecimals(currentSliderPercentage)) {
            percentage -= stepOnSliderInPercent;
          }
        }

        percentage = Math.max(percentage, 0);

        _this.handleNavKeyPressed(percentage, currentSliderPercentage, stepOnSliderInPercent, _this.state.hasFocusOnSlider);
      }), _defineProperty(_keyFunctionCollectio, Key.ArrowRight, function () {
        if (!definedStep) {
          percentage = currentSliderPercentage + 1;
        } else {
          percentage = _this.getNearestStep(currentSliderPercentage);

          if (_this.tidyDecimals(percentage) <= _this.tidyDecimals(currentSliderPercentage)) {
            percentage += stepOnSliderInPercent;
          }
        }

        percentage = Math.min(percentage, 100);

        _this.handleNavKeyPressed(percentage, currentSliderPercentage, stepOnSliderInPercent, _this.state.hasFocusOnSlider);
      }), _keyFunctionCollectio);
      var keyFunction = keyFunctionCollection[ev.key];

      if (keyFunction) {
        keyFunction();
      }
    };

    _this.state = {
      hasFocusOnSlider: CurrentSlider.None,
      lowerSliderThumbPercentage: _this.props.look === "singleSlider" || _this.props.look === "rangeSlider" && props.value[0] < props.value[1] ? _this.calculateValueToPercent(_this.props.look === "singleSlider" ? props.value : props.value[0], props.minValue, props.maxValue) : undefined,
      upperSliderThumbPercentage: _this.props.look === "rangeSlider" && props.value[1] > props.value[0] ? _this.calculateValueToPercent(props.value[1], props.minValue, props.maxValue) : undefined,
      sliderMarkerTrackBaseColor: "white",
      initialLowerPercentage: !_this.props.onChange ? _this.calculateValueToPercent(_this.props.look === "singleSlider" ? props.value : props.value[0], props.minValue, props.maxValue) : 0,
      initialUpperPercentage: !_this.props.onChange ? _this.calculateValueToPercent(props.value[1] || undefined, props.minValue, props.maxValue) : 0
    };
    _this.inputId = props.id ? props.id : _this.inputId; // overwrite default when user has given id

    return _this;
  }

  _createClass(SliderWrapper, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      var _a;

      if ((_a = this.sliderMarkerTrackColorDummyRef) === null || _a === void 0 ? void 0 : _a.current) {
        var cssObj = window.getComputedStyle(this.sliderMarkerTrackColorDummyRef.current);

        if (cssObj) {
          this.setState({
            sliderMarkerTrackBaseColor: cssObj["background-color"]
          });
        }
      }

      this.registerMouseDownEventListeners();
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.deregisterMouseDownEventListeners();
    }
  }, {
    key: "getCalculatedRangeSliderPosition",
    value: function getCalculatedRangeSliderPosition(newPosition, slider, stepSliderInPercent) {
      var percentage = newPosition; // The approximation of 2 sliders is managed via distanceFactor, greater value increase the distance between sliders.

      var distanceInPercent = this.getThumbSliderWidth(slider, true) * this.props.distanceFactor; // when stepOnSlider is too small (0.01) for distance, the thumb width calculation should used for approximation

      var approximation = stepSliderInPercent > 1 ? stepSliderInPercent : distanceInPercent;

      if (this.state.hasFocusOnSlider === CurrentSlider.Lower) {
        // Step or precision movement
        if (this.props.step || this.props.precision) {
          // distance factor defined, slider positioned  before upper slider
          if (distanceInPercent > 0 && percentage > this.state.upperSliderThumbPercentage - approximation) {
            percentage = this.state.upperSliderThumbPercentage - approximation;
          } // no distance factor defined, slider overlapping upper slider


          if (distanceInPercent === 0 && percentage > this.state.upperSliderThumbPercentage) {
            percentage = this.state.upperSliderThumbPercentage;
          }
        } // Simple movement


        if (!(this.props.step || this.props.precision)) {
          // This move should be only done, if a stepper is defined, because this approximation is a coarse tuning
          percentage = percentage < this.state.upperSliderThumbPercentage - distanceInPercent ? percentage : this.state.upperSliderThumbPercentage - distanceInPercent;
        }
      }

      if (this.state.hasFocusOnSlider === CurrentSlider.Upper) {
        // Step or precision movement without approximation
        if (this.props.step || this.props.precision) {
          // distance factor defined, slider positioned  before lower slider
          if (distanceInPercent > 0 && percentage < this.state.lowerSliderThumbPercentage + approximation) {
            percentage = this.state.lowerSliderThumbPercentage + approximation;
          } // no distance factor defined, slider overlapping lower slider


          if (distanceInPercent === 0 && percentage < this.state.lowerSliderThumbPercentage) {
            percentage = this.state.lowerSliderThumbPercentage;
          }
        } // Simple movement with approximation


        if (!(this.props.step || this.props.precision)) {
          // This move should be only done, if a stepper is defined, because this approximation is a coarse tuning
          percentage = percentage > this.state.lowerSliderThumbPercentage + distanceInPercent ? percentage : this.state.lowerSliderThumbPercentage + distanceInPercent;
        }
      }

      return percentage;
    }
  }, {
    key: "render",
    value: function render() {
      return /*#__PURE__*/React.createElement(SliderPresentation, Object.assign({}, this.props, {
        id: this.inputId,
        hasFocusOnSlider: this.state.hasFocusOnSlider,
        sliderMarkerTrackBaseColor: this.state.sliderMarkerTrackBaseColor,
        lowerSliderThumbPercentage: this.state.lowerSliderThumbPercentage,
        upperSliderThumbPercentage: this.state.upperSliderThumbPercentage,
        onFocus: this.onFocus,
        // onBlur={this.onBlur}
        onMouseDown: this.onMouseDown,
        // ? onMouseMove
        hostRef: this.hostRef,
        lowerSliderThumbRef: this.lowerSliderThumbRef,
        upperSliderThumbRef: this.upperSliderThumbRef,
        sliderTrackContainerRef: this.sliderTrackContainerRef,
        sliderMarkerTrackColorDummyRef: this.sliderMarkerTrackColorDummyRef,
        onTrackClick: this.onTrackClick,
        onKeyDown: this.onKeyDown,
        onHelperMinClick: this.onHelperMinClick,
        onHelperMaxClick: this.onHelperMaxClick,
        handleOnSliderBlur: this.handleOnSliderBlur
      }));
    }
  }]);

  return SliderWrapper;
}(React.Component);

SliderWrapper.defaultProps = Object.assign(Object.assign({}, defaultProps), {
  distanceFactor: 0.0
});

export { SliderWrapper };
